import dayjs from "dayjs";

import type { QuestionPreviewItem } from "src-next/shared/ui/questionnaire-form-renderer";
import { parseQuestionOptions } from "src-next/shared/utils/questionnaireOptions";

import {
  QUESTIONNAIRE_RESPONSE_APPOINTMENT_REFERENCE_EXTENSION_URL,
  QUESTIONNAIRE_TITLE_EXTENSION_URL,
  QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
  USAGE_TYPE_TO_CODE,
} from "../../utils/questionnaireQuestionnaireConstants";
import { resolveOptionalAppointmentBasedOn } from "../../../questionnaire-tasks";
import { optionToSlugOrFallback } from "../../utils/questionnaireQuestionnaireTransformers";
import type {
  QuestionnairePayload,
  QuestionnaireQuestionPayload,
} from "../../types";
import type {
  QuestionnaireRespondFormValues,
  QuestionnaireAnswerFormValue,
  QuestionnaireResponsePayload,
  ToQuestionnaireResponsePayloadInput,
} from "../types";

function isMultipleAnswersEnabled(question: QuestionnaireQuestionPayload): boolean {
  return (
    question.type === "multipleChoice" && question.allowMultipleAnswers === true
  );
}

function normalizeAnswerValueByQuestion(
  question: QuestionnaireQuestionPayload,
  rawValue: QuestionnaireAnswerFormValue["value"],
): string | string[] {
  if (isMultipleAnswersEnabled(question)) {
    if (!Array.isArray(rawValue)) {
      const singleValue = String(rawValue ?? "").trim();
      return singleValue ? [singleValue] : [];
    }

    return rawValue
      .map((value) => String(value).trim())
      .filter((value) => value.length > 0);
  }

  if (Array.isArray(rawValue)) {
    const first = rawValue.find(
      (value) => String(value).trim().length > 0,
    );
    return first ? String(first).trim() : "";
  }

  return String(rawValue ?? "").trim();
}

function toQuestionAnswer(
  question: QuestionnaireQuestionPayload,
  value: string,
): QuestionnaireResponsePayload["item"][number]["answer"][number] | null {
  if (question.type === "yesNo") {
    return { valueBoolean: value === "yes" };
  }

  if (question.type === "multipleChoice") {
    const options = parseQuestionOptions(question.optionsCsv);
    const matchIdx = options.findIndex((o) => o === value);
    if (matchIdx < 0) return null;
    const display = options[matchIdx];
    return {
      valueCoding: {
        code: optionToSlugOrFallback(display, matchIdx),
        display,
      },
    };
  }

  return { valueString: value };
}

export function toQuestionPreviewItems(
  questions: QuestionnairePayload["questions"],
): QuestionPreviewItem[] {
  return questions.map((question) => ({
    id: question.id,
    label: question.label,
    type: question.type,
    isRequired: question.isRequired,
    helperText: question.helperText,
    optionsCsv: question.optionsCsv,
    allowMultipleAnswers: isMultipleAnswersEnabled(question),
  }));
}

export function toRespondFormValues(
  questions: QuestionnairePayload["questions"],
): QuestionnaireRespondFormValues {
  return {
    answers: questions.map((question) => ({
      questionId: question.id,
      questionType: question.type,
      allowMultipleAnswers: isMultipleAnswersEnabled(question),
      value: isMultipleAnswersEnabled(question) ? [] : "",
    })),
  };
}

export function toQuestionnaireResponsePayload(
  input: ToQuestionnaireResponsePayloadInput,
): QuestionnaireResponsePayload {
  const questionById = new Map(
    input.questions.map((question) => [question.id, question]),
  );

  const item = input.values.answers
    .map((answer) => {
      const question = questionById.get(answer.questionId);
      if (!question) return null;

      const shouldAllowMultipleAnswers = isMultipleAnswersEnabled(question);
      const normalizedAnswerValue = normalizeAnswerValueByQuestion(
        question,
        answer.value,
      );

      if (shouldAllowMultipleAnswers) {
        const selectedValues = Array.isArray(normalizedAnswerValue)
          ? normalizedAnswerValue
          : [];
        if (selectedValues.length === 0) return null;

        const multipleAnswers = selectedValues
          .map((selectedValue) => toQuestionAnswer(question, selectedValue))
          .filter(
            (questionAnswer): questionAnswer is NonNullable<typeof questionAnswer> =>
              questionAnswer !== null,
          );
        if (multipleAnswers.length === 0) return null;

        return {
          linkId: question.id,
          text: question.label || undefined,
          answer: multipleAnswers,
        };
      }

      const normalizedValue = Array.isArray(normalizedAnswerValue)
        ? ""
        : normalizedAnswerValue;
      if (!normalizedValue) return null;

      const firstAnswer = toQuestionAnswer(question, normalizedValue);
      if (!firstAnswer) return null;

      return {
        linkId: question.id,
        text: question.label || undefined,
        answer: [firstAnswer],
      };
    })
    .filter((row) => row !== null) as QuestionnaireResponsePayload["item"];

  return {
    resourceType: "QuestionnaireResponse",
    status: "in-progress",
    questionnaire: `${input.questionnaireId}`,
    subject: {
      reference: input.subjectReference,
    },
    authored: dayjs().toISOString(),
    item,
  };
}

/**
 * Recurso FHIR para POST no ehr-poster: `subject` + `author` (opcional, proxy legal) +
 * extensões Fluxmed (tipo de uso; título; opcionalmente Appointment via
 * `http://fluxmed.com.br/fhir/StructureDefinition/AppointmentReference`).
 */
export function toFhirQuestionnaireResponseForPoster(input: {
  questionnaireId: string;
  subjectReference: string;
  questions: QuestionnaireQuestionPayload[];
  values: QuestionnaireRespondFormValues;
  usageType: QuestionnairePayload["usageType"];
  questionnaireTitle?: string;
  appointmentReference?: string;
  /**
   * Patient do responsável que responde pelo dependente (`subject` permanece o dependente).
   * O ehr-poster deve persistir como `QuestionnaireResponse.author` conforme FHIR R5.
   */
  authorReference?: string;
}): fhir5.QuestionnaireResponse {
  const draft = toQuestionnaireResponsePayload({
    questionnaireId: input.questionnaireId,
    subjectReference: input.subjectReference,
    questions: input.questions,
    values: input.values,
  });

  const title = String(input.questionnaireTitle ?? "").trim();
  const normalizedAppointment = input.appointmentReference
    ? resolveOptionalAppointmentBasedOn(input.appointmentReference)
    : undefined;

  /** Referência `Patient/...` do titular que envia a resposta em nome do `subject`. */
  const authorRef = String(input.authorReference ?? "").trim();

  return {
    resourceType: "QuestionnaireResponse",
    status: "completed",
    questionnaire: draft.questionnaire,
    subject: draft.subject,
    authored: draft.authored,
    ...(authorRef.startsWith("Patient/") && authorRef.length > "Patient/".length
      ? { author: { reference: authorRef } }
      : {}),
    extension: [
      {
        url: QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
        valueCode: USAGE_TYPE_TO_CODE[input.usageType],
      },
      ...(title
        ? [
            {
              url: QUESTIONNAIRE_TITLE_EXTENSION_URL,
              valueString: title,
            } as fhir5.Extension,
          ]
        : []),
      ...(normalizedAppointment
        ? [
            {
              url: QUESTIONNAIRE_RESPONSE_APPOINTMENT_REFERENCE_EXTENSION_URL,
              valueReference: { reference: normalizedAppointment },
            } as fhir5.Extension,
          ]
        : []),
    ],
    item: draft.item,
  };
}
